import { postData } from "../api/api";
import { getDisplayCalendarDayWindow } from "../lib/datetime/displayDateTime";

export type DashboardActivityStatusColor =
  | "green"
  | "yellow"
  | "orange"
  | "red";

export interface SourceSystemWindowCounts {
  last30Min: number;
  last1Hour: number;
  today: number;
  prevWeek30Min: number;
  prevWeek1Hour: number;
  prevWeekToday: number;
}

export interface DashboardSeverityTodayCounts {
  low: number;
  medium: number;
  high: number;
  critical: number;
}

export interface DashboardSeverityTimelinePoint extends DashboardSeverityTodayCounts {
  timestamp: number;
}

export interface DashboardSeverityTimeline {
  points: DashboardSeverityTimelinePoint[];
}

export type SourceSystemLineItemKind = "contact" | "source" | "base";

export interface SourceSystemLineItemRequest {
  rowKey: string;
  sourceSystemId: string;
  kind: SourceSystemLineItemKind;
  value?: string;
}

function applyCustomerIdFilter(searchQuery: any, customerId?: string): any {
  const id = typeof customerId === "string" ? customerId.trim() : "";
  if (!id) return searchQuery;

  const customerFilter = { term: { customerId: id } };
  const must = searchQuery?.query?.bool?.must;
  if (!Array.isArray(must)) return searchQuery;

  return {
    ...searchQuery,
    query: {
      ...searchQuery.query,
      bool: {
        ...searchQuery.query.bool,
        must: [...must, customerFilter],
      },
    },
  };
}

export function resolveActivityStatusColor(
  current: Pick<SourceSystemWindowCounts, "last30Min" | "last1Hour" | "today">,
  previous: Pick<
    SourceSystemWindowCounts,
    "prevWeek30Min" | "prevWeek1Hour" | "prevWeekToday"
  >,
): DashboardActivityStatusColor {
  let increased = 0;
  if (current.last30Min > previous.prevWeek30Min) increased += 1;
  if (current.last1Hour > previous.prevWeek1Hour) increased += 1;
  if (current.today > previous.prevWeekToday) increased += 1;

  if (increased >= 3) return "red";
  if (increased === 2) return "orange";
  if (increased === 1) return "yellow";
  return "green";
}

function emptyCounts(): SourceSystemWindowCounts {
  return {
    last30Min: 0,
    last1Hour: 0,
    today: 0,
    prevWeek30Min: 0,
    prevWeek1Hour: 0,
    prevWeekToday: 0,
  };
}

function escapeWildcard(value: string): string {
  return value.replace(/([\\*?])/g, "\\$1");
}

/** Case-insensitive substring match on raw subject and body.content (HTML included). */
function buildSourceValueMatchClause(value: string): Record<string, unknown> {
  const pattern = `*${escapeWildcard(value)}*`;
  return {
    bool: {
      should: [
        { wildcard: { subject: { value: pattern, case_insensitive: true } } },
        {
          wildcard: {
            "body.content": { value: pattern, case_insensitive: true },
          },
        },
      ],
      minimum_should_match: 1,
    },
  };
}

/** Customer-TZ calendar day from 00:00:00 through now (not a rolling 24h window). */
function getLocalCalendarDayRange(
  dayOffset = 0,
  timeZone?: string,
): { gte: string; lte: string } {
  const range = getDisplayCalendarDayWindow(dayOffset, timeZone);
  return { gte: range.gte, lte: range.lte };
}

function buildLineItemMatchClause(
  item: SourceSystemLineItemRequest,
): Record<string, unknown> | null {
  const value = typeof item.value === "string" ? item.value.trim() : "";
  if (item.kind === "base") {
    return null;
  }

  if (item.kind === "contact") {
    if (!value) return { match_none: {} };
    return {
      bool: {
        should: [
          { term: { "from.emailAddress.address": value } },
          { term: { "from.emailAddress.address.keyword": value } },
          { match_phrase: { "from.emailAddress.address": value } },
        ],
        minimum_should_match: 1,
      },
    };
  }

  if (!value) return { match_none: {} };
  return buildSourceValueMatchClause(value);
}

function buildLineItemStatsQuery(
  _customerId: string,
  item: SourceSystemLineItemRequest,
  timeZone?: string,
): Record<string, unknown> {
  const rowMatch = buildLineItemMatchClause(item);
  const must: Record<string, unknown>[] = [];

  // Source rows: customer scope only. Contact/base rows: also filter by source system.
  if (item.kind !== "source") {
    must.push({ term: { sourceSystemId: item.sourceSystemId } });
  }

  if (rowMatch) {
    must.push(rowMatch);
  }

  const todayRange = getLocalCalendarDayRange(0, timeZone);
  const prevWeekTodayRange = getLocalCalendarDayRange(-7, timeZone);

  return {
    query: {
      bool: { must },
    },
    aggs: {
      last_30m: {
        filter: { range: { createdDateTime: { gte: "now-30m", lte: "now" } } },
      },
      last_1h: {
        filter: { range: { createdDateTime: { gte: "now-1h", lte: "now" } } },
      },
      today: {
        filter: { range: { createdDateTime: todayRange } },
      },
      prev_week_30m: {
        filter: {
          range: { createdDateTime: { gte: "now-7d-30m", lte: "now-7d" } },
        },
      },
      prev_week_1h: {
        filter: {
          range: { createdDateTime: { gte: "now-7d-1h", lte: "now-7d" } },
        },
      },
      prev_week_today: {
        filter: { range: { createdDateTime: prevWeekTodayRange } },
      },
    },
    size: 0,
  };
}

function parseLineItemStatsResponse(response: any): SourceSystemWindowCounts {
  return {
    last30Min: response.aggregations?.last_30m?.doc_count ?? 0,
    last1Hour: response.aggregations?.last_1h?.doc_count ?? 0,
    today: response.aggregations?.today?.doc_count ?? 0,
    prevWeek30Min: response.aggregations?.prev_week_30m?.doc_count ?? 0,
    prevWeek1Hour: response.aggregations?.prev_week_1h?.doc_count ?? 0,
    prevWeekToday: response.aggregations?.prev_week_today?.doc_count ?? 0,
  };
}

const EMPTY_SEVERITY_COUNTS: DashboardSeverityTodayCounts = {
  low: 0,
  medium: 0,
  high: 0,
  critical: 0,
};

const EMPTY_SEVERITY_TIMELINE: DashboardSeverityTimeline = { points: [] };

function parseSeverityBuckets(
  buckets: Array<{ key?: unknown; doc_count?: number }>,
): DashboardSeverityTodayCounts {
  const counts: DashboardSeverityTodayCounts = {
    low: 0,
    medium: 0,
    high: 0,
    critical: 0,
  };

  for (const bucket of buckets) {
    const severity = String(bucket.key ?? "").toUpperCase();
    const count = bucket.doc_count ?? 0;
    if (severity === "LOW") counts.low = count;
    else if (severity === "MEDIUM") counts.medium = count;
    else if (severity === "HIGH") counts.high = count;
    else if (severity === "CRITICAL") counts.critical = count;
  }

  return counts;
}

async function fetchSeverityDistributionForRange(
  customerId: string,
  range: { gte: string; lte: string },
): Promise<DashboardSeverityTodayCounts> {
  const searchQuery = {
    query: {
      bool: {
        must: [{ range: { createdDateTime: range } }],
      },
    },
    aggs: {
      severity_breakdown: {
        terms: { field: "severity", size: 10 },
      },
    },
    size: 0,
  };

  const response = await postData(
    "/opensearch/search",
    applyCustomerIdFilter(searchQuery, customerId),
    { skipGlobalLoading: true },
  );

  return parseSeverityBuckets(
    response.aggregations?.severity_breakdown?.buckets ?? [],
  );
}

export function buildSeverityTimelineQuery(
  customerId: string,
  timeZone?: string,
) {
  const range = getLocalCalendarDayRange(0, timeZone);
  const zone = timeZone?.trim();
  const searchQuery = {
    query: {
      bool: {
        must: [{ range: { createdDateTime: range } }],
      },
    },
    aggs: {
      by_interval: {
        date_histogram: {
          field: "createdDateTime",
          fixed_interval: "15m",
          min_doc_count: 0,
          extended_bounds: {
            min: range.gte,
            max: range.lte,
          },
          ...(zone ? { time_zone: zone } : {}),
        },
        aggs: {
          by_severity: {
            terms: { field: "severity", size: 10 },
          },
        },
      },
    },
    size: 0,
  };

  return applyCustomerIdFilter(searchQuery, customerId);
}

export function parseSeverityTimelineResponse(
  response: unknown,
): DashboardSeverityTimeline {
  const aggregations =
    response && typeof response === "object"
      ? (response as { aggregations?: { by_interval?: { buckets?: unknown } } })
          .aggregations
      : undefined;
  const buckets = aggregations?.by_interval?.buckets;
  if (!Array.isArray(buckets)) {
    return EMPTY_SEVERITY_TIMELINE;
  }

  return {
    points: buckets.map((bucket) => {
      const raw =
        bucket && typeof bucket === "object"
          ? (bucket as Record<string, unknown>)
          : {};
      const timestamp = Number(raw.key);
      const nested =
        raw.by_severity && typeof raw.by_severity === "object"
          ? (
              raw.by_severity as {
                buckets?: Array<{ key?: unknown; doc_count?: number }>;
              }
            ).buckets
          : undefined;
      return {
        timestamp: Number.isFinite(timestamp) ? timestamp : 0,
        ...parseSeverityBuckets(Array.isArray(nested) ? nested : []),
      };
    }),
  };
}

export interface DashboardSeverityComparison {
  today: DashboardSeverityTodayCounts;
  weekAgo: DashboardSeverityTodayCounts;
}

class DashboardOpenSearchService {
  async getSourceSystemLineItemStats(
    customerId: string,
    items: SourceSystemLineItemRequest[],
    timeZone?: string,
  ): Promise<Record<string, SourceSystemWindowCounts>> {
    if (!customerId || items.length === 0) {
      return {};
    }

    const entries = await Promise.all(
      items.map(async (item) => {
        const value = typeof item.value === "string" ? item.value.trim() : "";
        if (item.kind !== "base" && !value) {
          return [item.rowKey, emptyCounts()] as const;
        }

        const searchQuery = buildLineItemStatsQuery(customerId, item, timeZone);
        const response = await postData(
          "/opensearch/search",
          applyCustomerIdFilter(searchQuery, customerId),
          { skipGlobalLoading: true },
        );
        return [item.rowKey, parseLineItemStatsResponse(response)] as const;
      }),
    );

    return Object.fromEntries(entries);
  }

  /** @deprecated Use getSourceSystemLineItemStats */
  async getSourceSystemActivityStats(
    customerId: string,
    sourceSystemIds: string[],
    timeZone?: string,
  ): Promise<Record<string, SourceSystemWindowCounts>> {
    const items: SourceSystemLineItemRequest[] = sourceSystemIds.map((id) => ({
      rowKey: id,
      sourceSystemId: id,
      kind: "base",
    }));
    const byRow = await this.getSourceSystemLineItemStats(
      customerId,
      items,
      timeZone,
    );
    const result: Record<string, SourceSystemWindowCounts> = {};
    for (const id of sourceSystemIds) {
      result[id] = byRow[id] ?? emptyCounts();
    }
    return result;
  }

  async getSeverityTimeline(
    customerId: string,
    timeZone?: string,
  ): Promise<DashboardSeverityTimeline> {
    if (!customerId) {
      return EMPTY_SEVERITY_TIMELINE;
    }

    const response = await postData(
      "/opensearch/search",
      buildSeverityTimelineQuery(customerId, timeZone),
      { skipGlobalLoading: true },
    );

    return parseSeverityTimelineResponse(response);
  }

  async getTodaySeverityDistribution(
    customerId: string,
    timeZone?: string,
  ): Promise<DashboardSeverityTodayCounts> {
    if (!customerId) {
      return { ...EMPTY_SEVERITY_COUNTS };
    }

    return fetchSeverityDistributionForRange(
      customerId,
      getLocalCalendarDayRange(0, timeZone),
    );
  }

  async getWeekAgoSeverityDistribution(
    customerId: string,
    timeZone?: string,
  ): Promise<DashboardSeverityTodayCounts> {
    if (!customerId) {
      return { ...EMPTY_SEVERITY_COUNTS };
    }

    return fetchSeverityDistributionForRange(
      customerId,
      getLocalCalendarDayRange(-7, timeZone),
    );
  }

  async getSeverityDistributionComparison(
    customerId: string,
    timeZone?: string,
  ): Promise<DashboardSeverityComparison> {
    const empty = { ...EMPTY_SEVERITY_COUNTS };
    if (!customerId) {
      return { today: empty, weekAgo: empty };
    }

    const [today, weekAgo] = await Promise.all([
      fetchSeverityDistributionForRange(
        customerId,
        getLocalCalendarDayRange(0, timeZone),
      ),
      fetchSeverityDistributionForRange(
        customerId,
        getLocalCalendarDayRange(-7, timeZone),
      ),
    ]);

    return { today, weekAgo };
  }
}

export const dashboardOpenSearchService = new DashboardOpenSearchService();
